const DEFAULTS = {
  modalidades: [],
  situacaoIds: [],
  bucket: null,
  orgaoId: null,
  purchasingUnitIds: [],
  purchasingUnitUnassigned: false,
  uf: null,
  abertura: { start: null, end: null },
  publicacao: { start: null, end: null },
  processoOuNumero: '',
  objeto: '',
  tiposEdital: [],
  tiposPregao: [],
  pagina: 1,
  porPagina: 20,
};

const toArray = value =>
  (Array.isArray(value) ? value.join(',') : value || '')
    .split(',')
    .map(item => item.trim())
    .filter(Boolean);

const validDate = value => {
  if (!value || Number.isNaN(new Date(value).getTime())) return null;
  return String(value).slice(0, 10);
};

const toDateParam = value => {
  if (!value) return null;
  if (typeof value === 'string') return validDate(value);
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  return null;
};

const positiveNumber = (value, fallback) => {
  const number = Number(value);
  return Number.isInteger(number) && number > 0 ? number : fallback;
};

export const parseLicitacoesQuery = (query = {}) => ({
  ...DEFAULTS,
  modalidades: toArray(query.modalidade).map(value => value.toUpperCase()),
  situacaoIds: toArray(query.situacao_id).map(Number).filter(Number.isFinite),
  bucket: query.bucket || null,
  orgaoId: (() => {
    const raw = toArray(query.orgaoId || query.orgao)[0];
    if (!raw) return null;
    const number = Number(raw);
    return Number.isNaN(number) ? raw : number;
  })(),
  purchasingUnitIds: toArray(query.purchasingUnitId || query.purchasing_unit_id).map(value => {
    const number = Number(value);
    return Number.isNaN(number) ? value : number;
  }),
  purchasingUnitUnassigned:
    query.purchasingUnitUnassigned === true ||
    ['true', '1'].includes(String(query.purchasingUnitUnassigned).toLowerCase()),
  uf: (() => {
    const raw = toArray(query.uf)[0];
    return raw ? raw.toUpperCase() : null;
  })(),
  abertura: {
    start: validDate(query.abertura_inicio),
    end: validDate(query.abertura_fim),
  },
  publicacao: {
    start: validDate(query.publicacao_inicio),
    end: validDate(query.publicacao_fim),
  },
  processoOuNumero: query.processo_licitacao || '',
  objeto: query.objeto || '',
  tiposEdital: toArray(query.tipo_edital),
  tiposPregao: toArray(query.tipo_pregao),
  pagina: positiveNumber(query.pagina, 1),
  porPagina: positiveNumber(query.por_pagina, 20),
});

export const serializeLicitacoesQuery = filters => {
  const query = {};
  const addCsv = (key, values) => {
    if (values && values.length) query[key] = values.join(',');
  };
  const addDate = (key, value) => {
    const date = toDateParam(value);
    if (date) query[key] = date;
  };

  addCsv('modalidade', filters.modalidades);
  addCsv('situacao_id', filters.situacaoIds);
  if (filters.bucket) query.bucket = filters.bucket;
  if (filters.orgaoId != null && filters.orgaoId !== '') query.orgao = String(filters.orgaoId);
  addCsv('purchasingUnitId', filters.purchasingUnitIds);
  if (filters.purchasingUnitUnassigned) query.purchasingUnitUnassigned = 'true';
  if (filters.uf) query.uf = String(filters.uf).toUpperCase();
  addDate('abertura_inicio', filters.abertura && filters.abertura.start);
  addDate('abertura_fim', filters.abertura && filters.abertura.end);
  addDate('publicacao_inicio', filters.publicacao && filters.publicacao.start);
  addDate('publicacao_fim', filters.publicacao && filters.publicacao.end);
  if (filters.processoOuNumero) query.processo_licitacao = filters.processoOuNumero;
  if (filters.objeto) query.objeto = filters.objeto;
  addCsv('tipo_edital', filters.tiposEdital);
  addCsv('tipo_pregao', filters.tiposPregao);
  if (Number(filters.pagina) > 1) query.pagina = String(filters.pagina);
  if (Number(filters.porPagina) !== DEFAULTS.porPagina)
    query.por_pagina = String(filters.porPagina);

  return query;
};

export const defaultLicitacoesFilters = () => ({
  ...DEFAULTS,
  modalidades: [],
  situacaoIds: [],
  orgaoId: null,
  purchasingUnitIds: [],
  purchasingUnitUnassigned: false,
  uf: null,
  abertura: { ...DEFAULTS.abertura },
  publicacao: { ...DEFAULTS.publicacao },
  tiposEdital: [],
  tiposPregao: [],
});

/** Compara critérios de filtro ignorando paginação (para detectar rascunho vs aplicado). */
export const areLicitacoesFilterCriteriaEqual = (a = {}, b = {}) => {
  const normalize = filters =>
    serializeLicitacoesQuery({
      ...defaultLicitacoesFilters(),
      ...filters,
      abertura: { ...(filters.abertura || {}) },
      publicacao: { ...(filters.publicacao || {}) },
      pagina: 1,
      porPagina: DEFAULTS.porPagina,
    });

  return JSON.stringify(normalize(a)) === JSON.stringify(normalize(b));
};
